/*
 *  -----------------------------------------------------------------------
 *    <copyright file="cm-observations.service.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import i18n from 'i18next';
import moment from 'moment';
import { STUDY_SUMMARY_GRIDS } from '@components/central-monitoring/study-summary/study-summary.data';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import Constants from './constants';
import { CentralMonitoringObservationArea, ReportStatus } from 'types/graphql-global-types';
import { unixDateFromUtc, formatDateUTC_DEPRECATED } from 'utils/convertor';
import { formUpdatedCreatedField } from '@utils/filter';
import {
    dataSets,
    dataSets_OpraQueries_dataSets_DataSet,
    dataSetReviews,
} from '@queries/generated/central-monitoring.generated';
import {
    PanelForObservationFields,
    CentralMonitoringObservationAndActionFields_CentralMonitoringObservation_panel_Panel as CentralMonitoringObservationAndActionFields_panel,
} from '@queries/generated/fragments.generated';

// filter CM observations according to AC
export const filterByType = (
    observation: any,
    type: string,
    datasetDate: any,
    datasetDates: any,
    currentPanel?: any,
) => {
    // filter observations for Site Summary page
    const cmrDatasets = datasetDates
        ?.filter(
            (item: any) =>
                new Date(datasetDate.created) > new Date(item.created) && item.reportStatus === ReportStatus.FINAL,
        )
        .sort((a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime());
    // take previously created review with Final status
    const reviewWithFinal = cmrDatasets.length > 0 ? cmrDatasets[0] : null;

    if (type === 'site') {
        if (datasetDate?.isCMR) {
            return (
                observation.dataSetId === datasetDate.dataSetId ||
                (!observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false)) ||
                (observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false) &&
                    unixDateFromUtc(reviewWithFinal?.created, false) <
                        unixDateFromUtc(observation.closedDatePopulated, false))
            );
        }
        return (
            observation.dataSetId === datasetDate.id ||
            (observation.dataSet &&
                unixDateFromUtc(datasetDate._date, true) > unixDateFromUtc(observation.dataSet?.dataCutDate, true))
        );
        // filter observartions for Study Summary page
    }

    if (type === 'study') {
        if (datasetDate.isCMR) {
            return (
                observation.dataSetId === datasetDate.dataSetId ||
                (!observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false)) ||
                (observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false) &&
                    unixDateFromUtc(reviewWithFinal?.created, false) <
                        unixDateFromUtc(observation.closedDatePopulated, false))
            );
        }
        return (
            observation.dataSetId === datasetDate.id ||
            (observation.dataSet &&
                unixDateFromUtc(datasetDate._date, false) > unixDateFromUtc(observation.dataSet?.dataCutDate, false))
        );
    }

    // filter observations for Companion View
    if (datasetDate.isCMR) {
        return (
            observation.panelId === currentPanel.panelId &&
            (observation.dataSetId === datasetDate.dataSetId ||
                (observation.dataSet &&
                    !observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false)) ||
                (observation.closedDate &&
                    unixDateFromUtc(datasetDate._date, false) >
                        unixDateFromUtc(observation.dataSet?.dataCutDate, false) &&
                    unixDateFromUtc(reviewWithFinal?.created, false) <
                        unixDateFromUtc(observation.closedDatePopulated, false)))
        );
    }
    return (
        (observation.dataSetId === datasetDate.id ||
            (observation.dataSet &&
                unixDateFromUtc(datasetDate._date, false) > unixDateFromUtc(observation.dataSet.dataCutDate, false))) &&
        observation.panelId === currentPanel.panelId
    );
};

export const riskOverviewPanelId = `${Constants.ObjectId.Empty}-riskOverview`;
export const studyOverviewPanelId = `${Constants.ObjectId.Empty}-studyOverview`;
export const siteOverviewPanelId = `${Constants.ObjectId.Empty}-siteOverview`;
export const siteSummaryPanelId = `${Constants.ObjectId.Empty}-siteSummary`;

export const makeObservationPanelsOptions = (panels: PanelForObservationFields[]) => {
    const result: SelectWithSearchDataArrayInterface[] = panels.map((panel: PanelForObservationFields) => ({
        id: panel.id,
        text: panel.name || panel.graph?.plotTitle || '',
    }));

    result.unshift(
        {
            id: riskOverviewPanelId,
            text: i18n.t('cm:riskOverview'),
        },
        {
            id: studyOverviewPanelId,
            text: i18n.t('cm:studyOverview'),
        },
        {
            id: siteOverviewPanelId,
            text: i18n.t('admin:siteOverview'),
        },
        {
            id: siteSummaryPanelId,
            text: i18n.t('cm:siteSummary'),
        },
    );

    result.sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));

    return result;
};

export const getObservationTypeFromPanelId = (panelId: string): CentralMonitoringObservationArea => {
    switch (panelId) {
        case riskOverviewPanelId:
            return CentralMonitoringObservationArea.RISK_OVERVIEW;
        case studyOverviewPanelId:
            return CentralMonitoringObservationArea.STUDY_OVERVIEW;
        case siteOverviewPanelId:
            return CentralMonitoringObservationArea.SITE_OVERVIEW;
        case siteSummaryPanelId:
            return CentralMonitoringObservationArea.SITE_SUMMARY;
        default:
            return CentralMonitoringObservationArea.PANEL;
    }
};

export const getObservationTypeFromDashboard = (
    companionView: boolean,
    siteDashboard: boolean,
    currentGrid: string,
): CentralMonitoringObservationArea => {
    if (companionView) return CentralMonitoringObservationArea.PANEL;

    if (siteDashboard) return CentralMonitoringObservationArea.SITE_SUMMARY;

    switch (currentGrid) {
        case STUDY_SUMMARY_GRIDS.risk:
            return CentralMonitoringObservationArea.RISK_OVERVIEW;
        case STUDY_SUMMARY_GRIDS.study:
            return CentralMonitoringObservationArea.STUDY_OVERVIEW;
        default:
            return CentralMonitoringObservationArea.SITE_OVERVIEW;
    }
};

export const getPanelId = (
    panel: CentralMonitoringObservationAndActionFields_panel | null,
    type?: CentralMonitoringObservationArea,
): string => {
    if (panel) return panel.id ?? Constants.ObjectId.Empty;

    switch (type) {
        case CentralMonitoringObservationArea.RISK_OVERVIEW:
            return riskOverviewPanelId;
        case CentralMonitoringObservationArea.STUDY_OVERVIEW:
            return studyOverviewPanelId;
        case CentralMonitoringObservationArea.SITE_OVERVIEW:
            return siteOverviewPanelId;
        case CentralMonitoringObservationArea.SITE_SUMMARY:
            return siteSummaryPanelId;
        default:
            return Constants.ObjectId.Empty;
    }
};

export const getPanelName = (
    panel: CentralMonitoringObservationAndActionFields_panel | null | undefined,
    type: CentralMonitoringObservationArea | null,
) => {
    if (panel) return panel.name ?? '';

    switch (type) {
        case CentralMonitoringObservationArea.RISK_OVERVIEW:
            return i18n.t('cm:riskOverview');
        case CentralMonitoringObservationArea.STUDY_OVERVIEW:
            return i18n.t('cm:studyOverview');
        case CentralMonitoringObservationArea.SITE_OVERVIEW:
            return i18n.t('admin:siteOverview');
        case CentralMonitoringObservationArea.SITE_SUMMARY:
            return i18n.t('cm:siteSummary');
        default:
            return '';
    }
};

export interface ExtendedDatasetFieldsWithIcon
    extends Partial<Omit<dataSets_OpraQueries_dataSets_DataSet, '__typename'>> {
    /** formated date */
    dataCutDate?: string | Date | null;
    _dataCutDate: string | Date | null;
    isCMR?: boolean;
    isDataset: boolean;
    dataSetId?: string;
    icon: 'database' | 'clipboard-list';
    _date: string | null;
    _updatedByString?: string | null;
}

export const getDatasetDates = (
    inputDataSets: dataSets | null,
    inputDataSetsReview: dataSetReviews | null,
    sysAdminLevel: number,
): ExtendedDatasetFieldsWithIcon[] => {
    if (inputDataSets?.dataSets && inputDataSetsReview?.dataSetReviews) {
        const setsDates =
            inputDataSets.dataSets?.map((item) => ({
                ...item,
                _dataCutDate: formatDateUTC_DEPRECATED(item?.dataCutDate, true, 'DD-MMM-YYYY'),
                isCMR: false,
                isDataset: true,
                icon: 'database' as ExtendedDatasetFieldsWithIcon['icon'],
                _date: item?.dataCutDate,
            })) || [];
        // Get all of the dataset reviews that are associated with reports
        const reviewDates =
            [...(inputDataSetsReview?.dataSetReviews ?? [])]
                .filter(
                    (item) =>
                        item?.reportBlobInfoId !== Constants.ObjectId.Empty &&
                        item?.reportStatus === ReportStatus.FINAL,
                )
                .map((item) => ({
                    ...item,
                    _dataCutDate: formatDateUTC_DEPRECATED(
                        setsDates.find((setsDateItem) => setsDateItem.id === item?.dataSetId)?.dataCutDate ?? null,
                        true,
                        'DD-MMM-YYYY',
                    ),
                    isCMR: true,
                    isDataset: false,
                    icon: 'clipboard-list' as ExtendedDatasetFieldsWithIcon['icon'],
                    notes: setsDates.find((setsDateItem) => setsDateItem.id === item?.dataSetId)?.notes ?? '',
                    _date: setsDates.find((setsDateItem) => setsDateItem.id === item?.dataSetId)?.dataCutDate ?? null,
                    _updatedByString: formUpdatedCreatedField(item?.updatedBy ?? undefined),
                })) || [];
        const result = [...reviewDates].reduce((res: any, curr: any) => {
            const exists = res.findIndex((x: any) => x.dataSetId === curr.dataSetId);
            if (exists < 0) res.push({ ...curr });
            else if (moment(res[exists].created) < moment(curr.created)) res[exists] = { ...curr };

            return res;
        }, []);
        const allDates = setsDates.concat(result).filter((item) => item.active || sysAdminLevel > 0);
        allDates.sort(
            (a, b) =>
                (b._dataCutDate ? new Date(b._dataCutDate).getTime() : 0) -
                (a._dataCutDate ? new Date(a._dataCutDate).getTime() : 0),
        );

        return allDates && allDates.length > 0 ? allDates : [];
    }

    return [];
};
